import React from 'react';
import { WEEKDAY_SHORT, buildMonthGrid, isWeekend, parseIso } from '../../utils/ipoCalendar';
import { TYPE_STYLES } from './calendarStyles';

const MAX_CHIPS = 3;

// Full-month grid; clicking a day opens it in the timeline
export const MonthGridView = ({ monthKey, eventsByDate, today, onSelectDay }) => {
  const cells = buildMonthGrid(monthKey);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200/80">
        {WEEKDAY_SHORT.map((day) => (
          <div key={day} className="py-2 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((iso, index) => {
          if (!iso) {
            return <div key={`pad-${index}`} className="min-h-[72px] sm:min-h-[112px] bg-slate-50/60 border-r border-b border-slate-100" />;
          }
          const events = eventsByDate.get(iso) || [];
          const holiday = events.find((event) => event.type === 'holiday');
          const issueEvents = events.filter((event) => event.type !== 'holiday');
          const isToday = iso === today;
          const dayNumber = parseIso(iso).getDate();

          return (
            <div
              key={iso}
              className={`min-h-[72px] sm:min-h-[112px] border-r border-b border-slate-100 p-1 sm:p-1.5 ${
                holiday ? 'bg-slate-50' : isWeekend(iso) ? 'bg-slate-50/40' : 'bg-white'
              }`}
            >
              <button
                type="button"
                disabled={!events.length}
                onClick={() => onSelectDay(iso)}
                aria-label={`${iso}: ${events.length} events`}
                className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${
                  isToday
                    ? 'bg-[#008080] text-white'
                    : events.length
                      ? 'text-slate-800 hover:bg-slate-100'
                      : 'text-slate-400 cursor-default'
                }`}
              >
                {dayNumber}
              </button>

              {holiday && (
                <p className="mt-1 text-[10px] font-semibold text-slate-500 leading-tight line-clamp-2">
                  {holiday.holiday.name}
                </p>
              )}

              {/* Chips on wider screens, dots on phones */}
              <div className="hidden sm:block mt-1 space-y-0.5">
                {issueEvents.slice(0, MAX_CHIPS).map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => onSelectDay(iso)}
                    title={`${event.issue.company} — ${event.type}`}
                    className={`w-full flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-left truncate transition-colors ${TYPE_STYLES[event.type].chip}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TYPE_STYLES[event.type].dot}`} />
                    <span className="truncate">{event.issue.company}</span>
                  </button>
                ))}
                {issueEvents.length > MAX_CHIPS && (
                  <button
                    type="button"
                    onClick={() => onSelectDay(iso)}
                    className="text-[10px] font-bold text-[#008080] hover:underline px-1"
                  >
                    +{issueEvents.length - MAX_CHIPS} more
                  </button>
                )}
              </div>
              <div className="flex sm:hidden flex-wrap gap-0.5 mt-1">
                {issueEvents.map((event) => (
                  <span key={event.id} className={`w-1.5 h-1.5 rounded-full ${TYPE_STYLES[event.type].dot}`} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthGridView;
